import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useTranslations } from '@ui/i18n';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useMemo } from 'react';

import type { StatsMetricValue } from './types';

const endpoint = process.env.NEXT_PUBLIC_STATS_API ?? '';

type Response = {
  data: {
    result: {
      rows: StatsRow[];
    };
  };
};

type StatsRow = {
  withdrawable_day: string;
  withdrawable_ttl: string;
};

type ResultType = {
  vnoUnlocked: StatsMetricValue[];
};

function toUnixTimestamp(dateStr: string): number {
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

async function fetchMetrics(identifier: string): Promise<ResultType> {
  const response: AxiosResponse<Response> = await axios.get(
    `${endpoint}/${identifier}`,
  );

  const rows = response.data.data.result.rows;

  const vnoUnlocked = rows.map((row) => ({
    value: Number(row.withdrawable_ttl),
    timestamp: toUnixTimestamp(row.withdrawable_day).toString(),
  }));

  return { vnoUnlocked };
}

export const useStatsVnoFountainUnlocked = (
  options?: UseQueryOptions<ResultType>,
): StatsChartProps => {
  const t = useTranslations();
  const queryKey = ['useStatsVnoFountainUnlocked'];

  const result = useQuery({
    queryKey,
    queryFn: async () => await fetchMetrics('fountain-vno-unlocks'),
    ...options,
  }).data;

  return useMemo(
    () => ({
      title: t('Fountain VNO Unlocks'),
      values: {
        [t('Unlocked {coin}', {
          coin: 'VNO',
        })]: result?.vnoUnlocked,
      },
      type: 'bar',
    }),
    [result, t],
  );
};

export const useStatsVnoReservoirUnlocked = (
  options?: UseQueryOptions<ResultType>,
): StatsChartProps => {
  const t = useTranslations();
  const queryKey = ['useStatsVnoReservoirUnlocked'];

  const result = useQuery({
    queryKey,
    queryFn: async () => await fetchMetrics('reservoir-vno-unlocks'),
    ...options,
  }).data;

  return useMemo(
    () => ({
      title: t('Reservoir VNO Unlocks'),
      values: {
        [t('Unlocked {coin}', {
          coin: 'VNO',
        })]: result?.vnoUnlocked,
      },
      type: 'bar',
    }),
    [result, t],
  );
};
